import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faInfoCircle,
  faPen,
  faPlus,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useInvoiceExtraSave,
  useInvoiceExtraSetStatus,
} from 'api/invoice-extra/invoice-extra';
import { useGlobalSettingsGetInvoiceExtraDefaultValues } from 'api/global-settings/global-settings';
import { InvoiceExtraDto, InvoiceExtraStatus, InvoiceExtraType } from 'api/model';
import Dropdown from 'components/Dropdown';
import Select from 'components/inputs/Select';
import useTranslations from 'contexts/basicData/useTranslations';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

const DropdownWrapper = styled(Dropdown)`
  width: 100%;
`;

const DropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  min-height: 32px;
`;

const TriggerText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.foreground.primary};
`;

const TriggerSum = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground.secondary};
  white-space: nowrap;
  margin-left: 8px;
`;

const DropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  max-height: 400px;
  overflow-y: auto;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const Row = styled.div<{ variant: 'confirmed' | 'suggested' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  font-size: 12px;
  gap: 8px;

  ${({ variant, theme }) =>
    variant === 'confirmed'
      ? `
        background-color: ${theme.colors.background.button};
        color: white;
      `
      : `
        background-color: ${theme.colors.background.selection};
        color: white;
      `}
`;

const RowText = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const ActionButton = styled.button.attrs({ type: 'button' })<{ tone: 'primary' | 'on-colored' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  ${({ tone, theme }) =>
    tone === 'primary'
      ? `
        background-color: transparent;
        color: ${theme.colors.background.button};
        &:hover { background-color: ${theme.colors.background.button}30; }
      `
      : `
        background-color: rgba(255, 255, 255, 0.3);
        color: white;
        &:hover { background-color: rgba(255, 255, 255, 0.5); }
      `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Badge = styled.span<{ variant: 'confirmed' | 'suggested' }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
  vertical-align: middle;

  ${({ variant, theme }) =>
    variant === 'confirmed'
      ? `
    background-color: ${theme.colors.background.button};
    color: white;
  `
      : `
    background-color: ${theme.colors.background.selection};
    color: white;
  `}
`;

const BadgesContainer = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  margin: 4px 0;
`;

const Form = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
  padding: 6px;
  border: 1px dashed ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px;
  gap: 6px;
`;

const NumberInput = styled.input`
  padding: 4px 6px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  font-size: 12px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.foreground.primary};
`;

const TextInput = styled.input`
  padding: 4px 6px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  font-size: 12px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.foreground.primary};
`;

const SubmitButton = styled.button.attrs({ type: 'button' })`
  padding: 6px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  background-color: ${({ theme }) => theme.colors.background.button};
  color: white;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button.attrs({ type: 'button' })`
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  font-size: 11px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const DialogContent = styled.div`
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.sizes.radius.input}px;
  padding: 20px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const DialogTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.foreground.primary};
`;

const DialogCloseButton = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const DialogBody = styled.div`
  color: ${({ theme }) => theme.colors.foreground.secondary};
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const formatKr = (value: number): string => {
  if (Number.isInteger(value)) return `${value} kr`;
  return `${value.toFixed(2)} kr`;
};

interface Props {
  assignmentId: number;
  invoiceExtras: InvoiceExtraDto[];
  onDataChanged?: () => void;
  readOnly?: boolean;
}

interface EditState {
  id: number;
  type: InvoiceExtraType;
  value: number;
  reason: string;
}

const invoiceExtraTypeValues: InvoiceExtraType[] = Object.values(InvoiceExtraType).filter(
  (v): v is InvoiceExtraType => typeof v === 'number'
);

const InvoiceExtraDropdown = ({
  assignmentId,
  invoiceExtras,
  onDataChanged,
  readOnly = false,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [reasonDialog, setReasonDialog] = useState<InvoiceExtraDto | null>(null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [newType, setNewType] = useState<InvoiceExtraType | undefined>(undefined);
  const [newValue, setNewValue] = useState<string>('');
  const [newReason, setNewReason] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const { invoiceExtraTypes } = useTranslations();

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!containerRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  const saveMutation = useInvoiceExtraSave();
  const setStatusMutation = useInvoiceExtraSetStatus();
  const { data: defaultValues } = useGlobalSettingsGetInvoiceExtraDefaultValues();

  const { confirmed, suggested } = useMemo(() => {
    const c: InvoiceExtraDto[] = [];
    const s: InvoiceExtraDto[] = [];
    invoiceExtras.forEach((row) => {
      if (row.status === InvoiceExtraStatus.Confirmed) c.push(row);
      else if (row.status === InvoiceExtraStatus.Suggested) s.push(row);
    });
    return { confirmed: c, suggested: s };
  }, [invoiceExtras]);

  const confirmedSum = useMemo(
    () => confirmed.reduce((sum, r) => sum + (r.value ?? 0), 0),
    [confirmed]
  );

  const totalCount = confirmed.length + suggested.length;

  const typeLabel = useCallback(
    (t: InvoiceExtraType): string =>
      (invoiceExtraTypes as Record<string, string> | undefined)?.[String(t)] ?? String(t),
    [invoiceExtraTypes]
  );

  const rowLabel = useCallback(
    (row: InvoiceExtraDto): string => {
      const base = `${typeLabel(row.type)} · ${formatKr(row.value ?? 0)}`;
      return row.reason ? `${base} — ${row.reason}` : base;
    },
    [typeLabel]
  );

  const badgeLabel = useCallback(
    (row: InvoiceExtraDto): string => `${typeLabel(row.type)}: ${formatKr(row.value ?? 0)}`,
    [typeLabel]
  );

  const isLoading =
    saveMutation.status === 'loading' || setStatusMutation.status === 'loading';

  const handleSetStatus = useCallback(
    async (id: number, status: InvoiceExtraStatus) => {
      try {
        await setStatusMutation.mutateAsync({ params: { id, status } });
        onDataChanged?.();
      } catch {
        toast.error('Kunde inte uppdatera status');
      }
    },
    [setStatusMutation, onDataChanged]
  );

  const handleAdd = useCallback(async () => {
    if (newType === undefined) {
      toast.error('Välj typ');
      return;
    }
    const parsedValue = Number(newValue.replace(',', '.'));
    if (!Number.isFinite(parsedValue) || parsedValue === 0) {
      toast.error('Ange ett belopp');
      return;
    }
    try {
      await saveMutation.mutateAsync({
        data: {
          id: 0,
          assignmentID: assignmentId,
          type: newType,
          value: parsedValue,
          reason: newReason,
          status: InvoiceExtraStatus.Confirmed,
          created: new Date(0),
          createdByID: 0,
          modified: new Date(0),
          modifiedByID: 0,
        },
      });
      setNewType(undefined);
      setNewValue('');
      setNewReason('');
      onDataChanged?.();
    } catch {
      toast.error('Kunde inte lägga till');
    }
  }, [assignmentId, newType, newValue, newReason, saveMutation, onDataChanged]);

  const handleSaveEdit = useCallback(
    async (original: InvoiceExtraDto) => {
      if (!editing) return;
      try {
        await saveMutation.mutateAsync({
          data: {
            ...original,
            id: editing.id,
            type: editing.type,
            value: editing.value,
            reason: editing.reason,
          },
        });
        setEditing(null);
        onDataChanged?.();
      } catch {
        toast.error('Kunde inte spara ändringar');
      }
    },
    [editing, saveMutation, onDataChanged]
  );

  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  if (readOnly) {
    if (totalCount === 0) {
      return <TriggerText>-</TriggerText>;
    }
    return (
      <BadgesContainer>
        {confirmed.map((r) => (
          <Badge key={`c-${r.id}`} variant="confirmed" title={r.reason || ''}>
            {badgeLabel(r)}
          </Badge>
        ))}
        {suggested.map((r) => (
          <Badge key={`s-${r.id}`} variant="suggested" title={r.reason || ''}>
            {badgeLabel(r)}
          </Badge>
        ))}
      </BadgesContainer>
    );
  }

  const triggerContent =
    totalCount === 0 ? (
      <TriggerText>Klicka för att lägga till</TriggerText>
    ) : (
      <>
        <BadgesContainer>
          {confirmed.slice(0, 2).map((r) => (
            <Badge key={`c-${r.id}`} variant="confirmed" title={r.reason || ''}>
              {badgeLabel(r)}
            </Badge>
          ))}
          {suggested.slice(0, 2).map((r) => (
            <Badge key={`s-${r.id}`} variant="suggested" title={r.reason || ''}>
              {badgeLabel(r)}
            </Badge>
          ))}
          {totalCount > 4 && <TriggerText>+{totalCount - 4}</TriggerText>}
        </BadgesContainer>
        {confirmedSum > 0 && <TriggerSum>Σ {formatKr(confirmedSum)}</TriggerSum>}
      </>
    );

  const renderEditableRow = (row: InvoiceExtraDto, variant: 'confirmed' | 'suggested') => {
    if (editing?.id === row.id) {
      return (
        <Row key={`edit-${row.id}`} variant={variant}>
          <div style={{ flex: 1, display: 'grid', gap: 4 }}>
            <Select
              value={editing.type}
              onChange={(e) =>
                setEditing({ ...editing, type: Number(e.target.value) as InvoiceExtraType })
              }
            >
              {invoiceExtraTypeValues.map((t) => (
                <option key={t} value={t}>
                  {typeLabel(t)}
                </option>
              ))}
            </Select>
            <FormRow>
              <TextInput
                value={editing.reason}
                onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                placeholder="Anledning"
              />
              <NumberInput
                type="number"
                step="0.01"
                value={Number.isFinite(editing.value) ? editing.value : ''}
                onChange={(e) =>
                  setEditing({ ...editing, value: Number(e.target.value) })
                }
                placeholder="Belopp"
              />
            </FormRow>
          </div>
          <ActionButtons>
            <ActionButton
              tone="on-colored"
              onClick={() => handleSaveEdit(row)}
              disabled={isLoading}
              title="Spara"
            >
              <FontAwesomeIcon icon={faCheck} size="sm" />
            </ActionButton>
            <ActionButton
              tone="on-colored"
              onClick={() => setEditing(null)}
              disabled={isLoading}
              title="Avbryt"
            >
              <FontAwesomeIcon icon={faTimes} size="sm" />
            </ActionButton>
          </ActionButtons>
        </Row>
      );
    }
    return (
      <Row key={`row-${row.id}`} variant={variant} title={row.reason || ''}>
        <RowText>{rowLabel(row)}</RowText>
        <ActionButtons>
          {variant === 'suggested' && (
            <ActionButton
              tone="on-colored"
              onClick={() => handleSetStatus(row.id, InvoiceExtraStatus.Confirmed)}
              disabled={isLoading}
              title="Godkänn"
            >
              <FontAwesomeIcon icon={faCheck} size="sm" />
            </ActionButton>
          )}
          <ActionButton
            tone="on-colored"
            onClick={() =>
              setEditing({
                id: row.id,
                type: row.type,
                value: row.value ?? 0,
                reason: row.reason ?? '',
              })
            }
            disabled={isLoading}
            title="Redigera"
          >
            <FontAwesomeIcon icon={faPen} size="sm" />
          </ActionButton>
          <ActionButton
            tone="on-colored"
            onClick={() => handleSetStatus(row.id, InvoiceExtraStatus.Removed)}
            disabled={isLoading}
            title={variant === 'suggested' ? 'Avvisa' : 'Ta bort'}
          >
            <FontAwesomeIcon icon={faTimes} size="sm" />
          </ActionButton>
          {variant === 'suggested' && row.reason && (
            <ActionButton
              tone="on-colored"
              onClick={() => setReasonDialog(row)}
              title="Visa motivering"
            >
              <FontAwesomeIcon icon={faInfoCircle} size="sm" />
            </ActionButton>
          )}
        </ActionButtons>
      </Row>
    );
  };

  const dropdownContent = (
    <DropdownContent>
      {confirmed.length > 0 && (
        <Section>
          <SectionTitle>Bekräftade</SectionTitle>
          {confirmed.map((r) => renderEditableRow(r, 'confirmed'))}
        </Section>
      )}

      {suggested.length > 0 && (
        <Section>
          <SectionTitle>Föreslagna</SectionTitle>
          {suggested.map((r) => renderEditableRow(r, 'suggested'))}
        </Section>
      )}

      {totalCount > 0 && <Divider />}

      <Section>
        <SectionTitle>Lägg till ny</SectionTitle>
        <Form>
          <Select
            value={newType ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              const parsed = v === '' ? undefined : (Number(v) as InvoiceExtraType);
              setNewType(parsed);
              if (parsed !== undefined && newValue === '') {
                const def = defaultValues?.find((d) => d.type === parsed);
                if (def && def.value !== 0) {
                  setNewValue(String(def.value));
                }
              }
            }}
          >
            <option value="" disabled>
              Välj typ…
            </option>
            {invoiceExtraTypeValues.map((t) => (
              <option key={t} value={t}>
                {typeLabel(t)}
              </option>
            ))}
          </Select>
          <FormRow>
            <TextInput
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder="Anledning"
            />
            <NumberInput
              type="number"
              step="0.01"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="Belopp"
            />
          </FormRow>
          <SubmitButton onClick={handleAdd} disabled={isLoading}>
            <FontAwesomeIcon icon={faPlus} size="sm" />
            Lägg till
          </SubmitButton>
        </Form>
      </Section>
    </DropdownContent>
  );

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div ref={containerRef} onClick={handleContainerClick}>
        <DropdownWrapper
          capWidthToWrapper
          content={isOpen ? dropdownContent : undefined}
          onLostFocus={() => setIsOpen(false)}
          onClick={() => setIsOpen(!isOpen)}
        >
          <DropdownTrigger>
            {triggerContent}
            <FontAwesomeIcon
              icon={isOpen ? faChevronUp : faChevronDown}
              size="xs"
              style={{ marginLeft: 8 }}
            />
          </DropdownTrigger>
        </DropdownWrapper>
      </div>

      {reasonDialog && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <DialogOverlay onClick={() => setReasonDialog(null)}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle>Motivering</DialogTitle>
              <DialogCloseButton onClick={() => setReasonDialog(null)}>
                <FontAwesomeIcon icon={faTimes} />
              </DialogCloseButton>
            </DialogHeader>
            <DialogBody>{reasonDialog.reason || 'Ingen motivering tillgänglig.'}</DialogBody>
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <GhostButton onClick={() => setReasonDialog(null)}>Stäng</GhostButton>
            </div>
          </DialogContent>
        </DialogOverlay>
      )}
    </>
  );
};

export default InvoiceExtraDropdown;
